import React from 'react'
import { SubmissionProgressProps, selectSelectedCampaignSummary, selectSelectedSubmissionView } from 'state/review'
import SubmittedAtDisplay from './SubmittedAtDisplay'
import CommentAndVoteSummary from './CommentAndVoteSummary'
import { RejectOrVerify } from './RejectOrVerify'
import LinkInBio from './LinkInBio'
import Comment from './Comment'
import ReviewButtons from './ReviewButtons'
import SubmissionContent from './SubmissionContent'
import { StepState, TaskState } from 'state/tasks'
import PostTime from './PostTime'
import { Box } from 'rebass'
import EditRequestHistoryButton from 'components/Submissions/Review/Submissions/EditRequestHistory'
import ViewDetailButton from './ViewDetailButton'
import { useSelector } from 'react-redux'
import VerifyContent from '../Verify/VerifyContent'
import { ReviewedByDisplay } from './ReviewedByDisplay'
import styled from 'particles/themes/styled'
import { SUBMISSION_TASK_STATUS_ENUM, SUBMISSION_VIEW_ENUM } from 'state/submissions'
import ResubmittedIndicator from '../ResubmittedIndicator'

interface VerifyProgressBlockProps {
    submissionProgress: SubmissionProgressProps & {
        partiposterId: string // make it required to show profile inside edit request history modal
    }
    step: StepState
    task: TaskState
    hasResubmitted?: boolean
}

interface ProgressBlockProps extends VerifyProgressBlockProps {
    task: TaskState
    className?: string
}

export const VerifyProgressBlock: React.FC<VerifyProgressBlockProps> = ({ submissionProgress, step, task }) => {
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    return (
        <>
            <VerifyContent submissionProgress={submissionProgress} task={task} step={step} />
            {selectedCampaignSummary && (
                <SubmittedAtDisplay
                    submissionProgress={submissionProgress}
                    isTaskPending={submissionProgress.submission_task?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING}
                    country={selectedCampaignSummary.country}
                />
            )}
        </>
    )
}

const ProgressBlock: React.FC<ProgressBlockProps> = ({ submissionProgress, step, task, hasResubmitted, className }) => {
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)

    const hideEditRequestHistory =
        (submissionProgress?.submission_task?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING ||
            selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED) &&
        !submissionProgress.resubmitted

    return (
        <div className={className}>
            <div className="resubmitted-container">
                {!hideEditRequestHistory && (
                    <>
                        {submissionProgress.resubmitted && <ResubmittedIndicator />}
                        <EditRequestHistoryButton
                            submissionProgressUuid={submissionProgress.uuid}
                            partiposterId={submissionProgress.partiposterId}
                        />
                    </>
                )}
            </div>
            <SubmissionContent submissionProgress={submissionProgress} step={step} />
            <Box my="auto" />
            <LinkInBio submissionProgress={submissionProgress} step={step} task={task} />
            <PostTime submissionProgress={submissionProgress} step={step} task={task} />
            <Comment submissionProgress={submissionProgress} step={step} />
            <CommentAndVoteSummary submissionProgress={submissionProgress} step={step} />
            <RejectOrVerify submissionProgressUuid={submissionProgress.uuid} step={step} />
            <ReviewButtons submissionProgress={submissionProgress} />
            <ViewDetailButton submissionProgressUuid={submissionProgress.uuid} step={step} />

            {selectedCampaignSummary && (
                <>
                    <ReviewedByDisplay
                        submissionProgress={submissionProgress}
                        country={selectedCampaignSummary.country}
                    />
                    <SubmittedAtDisplay
                        submissionProgress={submissionProgress}
                        isTaskPending={
                            submissionProgress.submission_task?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING
                        }
                        country={selectedCampaignSummary.country}
                    />
                </>
            )}
        </div>
    )
}

export default styled(ProgressBlock)`
    .resubmitted-container {
        display: flex;
        gap: ${({ theme }) => theme.spacing[300]};
        align-items: baseline;
        margin-top: 14px;
        ${({ hasResubmitted }) => (hasResubmitted ? { height: 20 } : undefined)}
    }
`
